
/* global removeTrapFocus */

const MainSearch = class extends HTMLElement {
  constructor() {
    super();

    this.activeResultIndex = -1;
    this.searchTimeoutID = null;
    this.searchAbortController = null;
    this.oldSearchValue = '';

    this.searchInput = this.querySelector('.main-search__input');
    this.resultsBox = this.querySelector('.main-search__results');

    this.querySelectorAll('.main-search__close').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        this.cancelSearch();
        document.body.classList.remove('show-search');
        removeTrapFocus(theme.trapOpener);
      });
    });

    this.searchInput.addEventListener('keyup', (event) => {
      if (event.key === 'Escape') {
        this.querySelector('.main-search__close')?.click();
      }
    });

    this.searchInput.addEventListener(
      'keydown',
      this.handleKeyDown.bind(this)
    );

    if (this.dataset.quickSearch === 'true') {
      this.initQuickSearch();
    }
  }

  disconnectedCallback() {
    this.cancelSearch();
  }

  cancelSearch() {
    clearTimeout(this.searchTimeoutID);

    if (this.searchAbortController) {
      this.searchAbortController.abort();
      this.searchAbortController = null;
    }
  }

  handleKeyDown(event) {
    const options = [
      ...this.querySelectorAll(
        '.main-search__results [role="option"]'
      )
    ];

    if (!options.length) return;

    const updateActive = () => {
      options.forEach((option) => {
        option.classList.remove('search-result-active');
      });

      const active = options[this.activeResultIndex];

      if (!active) return;

      active.classList.add('search-result-active');
      event.currentTarget.setAttribute(
        'aria-activedescendant',
        active.id
      );
    };

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.activeResultIndex = Math.min(
        this.activeResultIndex + 1,
        options.length - 1
      );
      updateActive();
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.activeResultIndex = Math.max(
        this.activeResultIndex - 1,
        0
      );
      updateActive();
    }

    if (event.key === 'Enter' && this.activeResultIndex >= 0) {
      const active = options[this.activeResultIndex];
      const link = active?.querySelector('a[href]');

      if (link) {
        event.preventDefault();
        link.click();
      }
    }
  }

  initQuickSearch() {
    this.searchInput.addEventListener(
      'input',
      () => this.handleInputChange()
    );
  }

  handleInputChange() {
    const term = this.searchInput.value.trim();

    this.cancelSearch();

    if (!term) {
      this.oldSearchValue = '';
      this.resultsBox.innerHTML = '';

      this.classList.remove(
        'main-search--has-results',
        'main-search--results-on-multiple-lines',
        'main-search--loading',
        'main-search--no-results'
      );

      this.searchInput.setAttribute('aria-expanded', 'false');
      return;
    }

    if (term === this.oldSearchValue) return;

    this.oldSearchValue = term;

    this.classList.remove(
      'main-search--has-results',
      'main-search--no-results',
      'main-search--results-on-multiple-lines'
    );

    this.classList.add('main-search--loading');

    this.resultsBox.innerHTML =
      '<div class="main-search__results-spinner">' +
      '<div class="loading-spinner"></div></div>';

    this.searchTimeoutID = setTimeout(() => {
      this.fetchSearchResults(term);
    }, 300);
  }

  createSearchURL(term, titleOnly = false) {
    const predictiveSupported =
      theme.Shopify.features.predictiveSearch;

    if (!predictiveSupported) {
      const url = new URL(
        this.searchInput.closest('form').action
      );

      url.searchParams.set('q', term);
      url.searchParams.set('section_id', 'main-search');

      return url;
    }

    const url = new URL(
      theme.routes.base + theme.routes.predictiveSearch,
      window.location.origin
    );

    const query = titleOnly
      ? `title:"${term.replace(/["\\]/g, ' ').trim()}"`
      : term;

    url.searchParams.set('q', query);
    url.searchParams.set('section_id', 'predictive-search');
    url.searchParams.set('resources[limit]', '10');
    url.searchParams.set('resources[limit_scope]', 'each');

    // Search product titles alongside the standard results.
    // Shopify may ignore this legacy field-selection setting
    // for product results.
    url.searchParams.set(
      'resources[options][fields]',
      this.dataset.quickSearchMeta === 'true'
        ? 'title,product_type,variants.title,vendor,tag,variants.sku'
        : 'title,product_type,variants.title,vendor'
    );

    return url;
  }

  async getSearchMarkup(url, signal) {
    const response = await fetch(url, {
      method: 'GET',
      signal
    });

    if (!response.ok) {
      throw new Error(`Search HTTP ${response.status}`);
    }

    const html = await response.text();

    const template = document.createElement('template');
    template.innerHTML = html;

    const predictiveSupported =
      theme.Shopify.features.predictiveSearch;

    return {
      list: template.content.querySelector(
        predictiveSupported
          ? '.product-grid'
          : '.section-search-template .product-grid'
      ),
      message: template.content.querySelector(
        predictiveSupported
          ? '[data-results-message]'
          : '.section-search-template [data-results-message]'
      )
    };
  }

  normalizeTitle(value) {
    return (value || '')
      .toLowerCase()
      .replace(/[^\p{L}\p{N}]+/gu, ' ')
      .trim()
      .replace(/\s+/g, ' ');
  }

  getProductTitle(block) {
    const titleElement = block.querySelector(
      '.product-block__title, ' +
      '.product-block__title a, ' +
      '.product-info__title, ' +
      '.product-title'
    );

    if (titleElement) {
      return titleElement.textContent.trim();
    }

    const anchors = [
      ...block.querySelectorAll('a[href*="/products/"]')
    ];

    for (const anchor of anchors) {
      const title =
        anchor.getAttribute('title') ||
        anchor.getAttribute('aria-label') ||
        anchor.textContent.trim();

      if (title) return title;
    }

    return '';
  }

  getProductKey(block) {
    const link = block.querySelector('a[href*="/products/"]');

    if (link) {
      try {
        return new URL(
          link.href,
          window.location.origin
        ).pathname;
      } catch (error) {
        return link.getAttribute('href').split('?')[0];
      }
    }

    return '';
  }

  rankProduct(block, searchTerm, isTitleResult) {
    const title = this.normalizeTitle(
      this.getProductTitle(block)
    );

    const query = this.normalizeTitle(searchTerm);

    if (!title || !query) {
      return isTitleResult ? 3 : 4;
    }

    if (title === query) return 0;
    if (title.startsWith(query)) return 1;
    if (title.includes(query)) return 2;

    return isTitleResult ? 3 : 4;
  }

  async fetchSearchResults(searchTerm) {
    const controller = new AbortController();
    this.searchAbortController = controller;

    const predictiveSupported =
      theme.Shopify.features.predictiveSearch;

    const normalURL = this.createSearchURL(
      searchTerm,
      false
    );

    const requests = [
      this.getSearchMarkup(normalURL, controller.signal)
    ];

    if (predictiveSupported) {
      const titleURL = this.createSearchURL(
        searchTerm,
        true
      );

      requests.unshift(
        this.getSearchMarkup(titleURL, controller.signal)
      );
    }

    try {
      const responses = await Promise.allSettled(requests);

      if (
        controller.signal.aborted ||
        this.searchInput.value.trim() !== searchTerm
      ) {
        return;
      }

      const successful = responses
        .filter((result) => result.status === 'fulfilled')
        .map((result) => result.value);

      if (!successful.length) {
        throw new Error('Both search requests failed');
      }

      const normalResult = successful[
        successful.length - 1
      ];

      const products = [];
      const pages = [];
      const seen = new Set();

      successful.forEach((result, sourceIndex) => {
        if (!result.list) return;

        const isTitleResult =
          predictiveSupported &&
          responses[0].status === 'fulfilled' &&
          sourceIndex === 0;

        result.list.querySelectorAll(
          '.product-block:not(.collection-block):not(.page-block)'
        ).forEach((block) => {
          const key = this.getProductKey(block);

          if (!key || seen.has(key)) return;

          seen.add(key);

          if (
            block.querySelector(
              '.quickbuy-toggle'
            )
          ) {
            block.querySelectorAll(
              '.btn.quickbuy-toggle'
            ).forEach((element) => element.remove());

            block.querySelectorAll(
              '.quickbuy-toggle'
            ).forEach((element) => {
              element.classList.remove('quickbuy-toggle');
            });
          }

          products.push({
            element: block,
            rank: this.rankProduct(
              block,
              searchTerm,
              isTitleResult
            ),
            order: products.length
          });
        });

        // Show page/article results from normal search.
        if (result === normalResult) {
          result.list.querySelectorAll(
            '.product-block.page-block'
          ).forEach((block) => {
            pages.push(block);
          });
        }
      });

      products.sort((a, b) => {
        return a.rank - b.rank || a.order - b.order;
      });

      const resultsProducts = document.createElement('div');

      resultsProducts.className =
        'main-search__results__products collection-listing';

      const productGrid = document.createElement('div');

      productGrid.className =
        successful.find((result) => result.list)?.list.className ||
        'product-grid product-grid--per-row-4';

      products.slice(0, 8).forEach((product, index) => {
        const block = product.element;

        block.classList.add('main-search-result');
        block.setAttribute('role', 'option');
        block.id = `SearchResult${index}`;

        productGrid.appendChild(block);
      });

      resultsProducts.appendChild(productGrid);

      const resultsPages = document.createElement('div');
      resultsPages.className = 'main-search__results__pages';

      pages.forEach((block) => {
        const link = block.querySelector('a[href]');

        if (!link) return;

        const item = document.createElement('a');
        item.className =
          'main-search-result main-search-result--page';
        item.href = link.href;

        const title = block.querySelector(
          '.page-block__title'
        );

        const text = document.createElement('div');
        text.className = 'main-search-result__text';
        text.textContent = title?.textContent.trim() || '';

        item.appendChild(text);
        resultsPages.appendChild(item);
      });

      this.resultsBox.innerHTML = '';

      this.classList.remove(
        'main-search--loading',
        'main-search--has-results',
        'main-search--no-results',
        'main-search--results-on-multiple-lines'
      );

      const hasProducts = productGrid.children.length > 0;
      const hasPages = resultsPages.children.length > 0;

      if (hasProducts || hasPages) {
        this.classList.add('main-search--has-results');

        this.classList.toggle(
          'main-search--results-on-multiple-lines',
          productGrid.children.length > 4
        );

        if (hasProducts) {
          this.resultsBox.appendChild(resultsProducts);
        }

        if (hasPages) {
          const heading = document.createElement('h6');
          heading.className = 'main-search-result__heading';
          heading.innerHTML = theme.strings.generalSearchPages;

          resultsPages.prepend(heading);
          this.resultsBox.appendChild(resultsPages);
        }

        const allLink = document.createElement('a');

        allLink.className =
          'main-search__results-all-link btn btn--secondary';

        const formURL = new URL(
          this.searchInput.closest('form').action
        );

        formURL.searchParams.set('q', searchTerm);

        allLink.href = formURL.toString();
        allLink.innerHTML =
          theme.strings.generalSearchViewAll;

        this.resultsBox.appendChild(allLink);

        const status = document.querySelector(
          '#HeaderSearchResultsStatus'
        );

        if (status && normalResult?.message) {
          status.innerHTML = normalResult.message.innerHTML;
        }

        this.searchInput.setAttribute(
          'aria-expanded',
          'true'
        );

        this.activeResultIndex = -1;
        this.searchInput.removeAttribute(
          'aria-activedescendant'
        );

      } else {
        this.classList.add('main-search--no-results');

        const emptyMessage = document.createElement('div');
        emptyMessage.className = 'main-search__empty-message';
        emptyMessage.innerHTML =
          theme.strings.generalSearchNoResultsWithoutTerms;

        this.resultsBox.appendChild(emptyMessage);

        this.searchInput.setAttribute(
          'aria-expanded',
          'false'
        );
      }

    } catch (error) {
      if (error.name === 'AbortError') return;

      console.warn('Header search error:', error);

      this.classList.remove('main-search--loading');

    } finally {
      if (this.searchAbortController === controller) {
        this.searchAbortController = null;
      }
    }
  }
};

if (!customElements.get('main-search')) {
  customElements.define('main-search', MainSearch);
}
